import { useState, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Codes } from '@/constants/codes';

interface CaseReportStatementsListProps {
    value: any[];
    isReadOnly?: boolean;
    involvedPersons?: any[];
    onChange?: (statements: any[]) => void;
    onAdd?: () => void;
    onEdit?: (statement: any) => void;
}

export function CaseReportStatementsList({
    value: statements,
    isReadOnly = false,
    involvedPersons = [],
    onChange,
    onAdd,
    onEdit,
}: CaseReportStatementsListProps) {
    const { theme } = useTheme();
    const [expandedIds, setExpandedIds] = useState<string[]>([]);
    const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; item: any }>({ open: false, item: null });

    const toggleExpand = useCallback((tempid: string) => {
        setExpandedIds(prev => {
            const idx = prev.indexOf(tempid);
            if (idx >= 0) {
                return prev.filter(id => id !== tempid);
            } else {
                return [...prev, tempid];
            }
        });
    }, []);

    const isExpanded = useCallback((tempid: string): boolean => {
        return expandedIds.includes(tempid);
    }, [expandedIds]);

    const confirmDelete = useCallback((statement: any) => {
        setDeleteConfirm({ open: true, item: statement });
    }, []);

    const doDelete = useCallback(() => {
        const item = deleteConfirm.item;
        if (item) {
            onChange?.(statements.filter((s: any) => s.tempid !== item.tempid));
        }
        setDeleteConfirm({ open: false, item: null });
    }, [deleteConfirm.item, statements, onChange]);

    const cancelDelete = useCallback(() => {
        setDeleteConfirm({ open: false, item: null });
    }, []);

    const getPersonName = useCallback((statement: any): string => {
        if (statement.personName) {
            return statement.personName;
        }
        const personId = statement.npsdPerson;
        if (personId && involvedPersons) {
            const found = involvedPersons.find((p: any) =>
                p.id === personId || p.id === Number(personId)
            );
            if (found) {
                return found.name || found.fullName ||
                    `${found.firstName || ''} ${found.lastName || ''}`.trim() ||
                    'Unknown';
            }
        }
        return 'Unknown';
    }, [involvedPersons]);

    const getRolesDisplay = useCallback((statement: any): string => {
        const personId = statement.npsdPerson;
        if (personId && involvedPersons) {
            const found = involvedPersons.find((p: any) =>
                p.id === personId || p.id === Number(personId)
            );
            if (found && found.roles && found.roles.length > 0) {
                const codes = Codes.getPersonCodes ? Codes.getPersonCodes() : [];
                const roleNames = found.roles.map((roleCode: number) => {
                    const role = codes.find((c: any) => c.code === roleCode);
                    return role ? role.value : '';
                }).filter(Boolean);
                return roleNames.length > 0 ? roleNames.join(', ') : '';
            }
        }
        return '';
    }, [involvedPersons]);

    return (
        <div className={'case-report-evidence-list statements-list' + (theme ? ' ' + theme : '')}>
            <div className="header">
                <h1>Statements</h1>
                {!isReadOnly && (
                    <button
                        onClick={() => onAdd?.()}
                        className="btn-add"
                        type="button"
                    >
                        <i className="fas fa-plus"></i> STATEMENT
                    </button>
                )}
            </div>

            <div className="table-container">
                <table>
                    <thead>
                        <tr>
                            <th style={{ width: '20%' }}>Person</th>
                            <th style={{ width: '15%' }}>Roles</th>
                            <th style={{ width: '50%' }}>Statement</th>
                            <th style={{ width: '15%' }}>Actions</th>
                        </tr>
                    </thead>
                    {statements.length === 0 ? (
                        <tbody>
                            <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>No statements added</td></tr>
                        </tbody>
                    ) : (
                        <tbody>
                            {statements.map((statement: any) => (
                                <tr key={statement.tempid}>
                                    <td>
                                        <div className="item-name">{getPersonName(statement)}</div>
                                    </td>
                                    <td>
                                        <div className="item-roles">{getRolesDisplay(statement) || '\u2014'}</div>
                                    </td>
                                    <td>
                                        <div className="statement-text-full">{statement.statement || '\u2014'}</div>
                                    </td>
                                    <td>
                                        <div className="actions">
                                            {!isReadOnly && <button className="btn-edit" onClick={() => onEdit?.(statement)}>Edit</button>}
                                            {!isReadOnly && <button className="btn-delete" onClick={() => confirmDelete(statement)}>Delete</button>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    )}
                </table>
            </div>

            <div className="mobile-cards">
                {statements.length === 0 ? (
                    <div className="empty-state">
                        <p>No statements added</p>
                    </div>
                ) : (
                    statements.map((statement: any) => (
                        <div key={'mobile-' + statement.tempid} className="card">
                            <div className="card-header">
                                <div className="card-header-content">
                                    <div className="card-item-name">{getPersonName(statement)}</div>
                                    {getRolesDisplay(statement) && (
                                        <span className="roles-badge">
                                            {getRolesDisplay(statement)}
                                        </span>
                                    )}
                                </div>
                                <div className="card-header-actions">
                                    {!isReadOnly && <button className="btn-edit-sm" onClick={() => onEdit?.(statement)}>Edit</button>}
                                    {!isReadOnly && <button className="btn-delete-sm" onClick={() => confirmDelete(statement)}>Delete</button>}
                                </div>
                            </div>

                            <div className="card-body">
                                <div className="card-field">
                                    <div className="card-field-label">Statement</div>
                                    <div className="card-field-value statement-text-full">{statement.statement || '\u2014'}</div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <ConfirmModal
                open={deleteConfirm.open}
                title="Remove Statement?"
                messageHtml="Are you sure you want to remove this statement from the report?"
                confirmText="Remove"
                confirmClass="danger"
                onConfirm={doDelete}
                onCancel={cancelDelete}
            />
        </div>
    );
}

(window as any).CaseReportStatementsListReact = CaseReportStatementsList;
